import { v1 } from "uuid";
import { ViewId } from "../redux/reducers/MosaicManagerReducer";
import { isEqual } from "lodash";

export enum SplitDirection {
  Horizontal = "horizontal",
  Vertical = "vertical",
}

export enum MosaicDirection {
  Left = "left",
  Right = "right",
  Top = "top",
  Bottom = "bottom",
  Center = "center",
}

export type IMosaicNode = IParentNode | ILeafNode;

export interface IParentNode {
  first: IParentNode | ILeafNode | null;
  second: IParentNode | ILeafNode | null;
  direction: SplitDirection;
  splitPercentage: number;
  id: string;
}

export interface ILeafNode {
  id: string;
  tabNodes: IMosaicTabNode[];
  activeIndex: number;
}

export interface IMosaicTabNode {
  id: string;
  title: string;
  componentName: string;
  // content (new window), context (70/30 split), null (50/50 split)
  type: "content" | "context" | "singleContext";
  props: any;
  // set close warning
  setCloseWarning?: (closeWarning: string | null) => void;
  // Preferred split percentage for this tab, used when re-inserting via drag/drop
  preferredSplitPercentage?: number;
}

export function isParentNode(arg: any): arg is IParentNode {
  return arg.first !== undefined && arg.second !== undefined;
}

export function isLeafNode(arg: any): arg is ILeafNode {
  return arg.tabNodes !== undefined;
}

export function isMosaicTabNode(arg: any): arg is IMosaicTabNode {
  return arg.type !== undefined;
}

/* MOSAIC TREE UPDATE FUNCTIONS */
export const updateTreeRemoveTab = (currentNode: IMosaicNode, path: string[], keyToRemove: string): IMosaicNode => {
  const updateFunc = (leafNode: ILeafNode): ILeafNode | null => {
    if (leafNode) {
      return removeTab(leafNode, keyToRemove);
    }
    return null;
  };
  return updateNode(currentNode, path, updateFunc);
};

export const updateTabNodeObject = (
  currentNode: IMosaicNode,
  path: string[],
  tabId: string,
  update: Partial<IMosaicTabNode>
): IMosaicNode => {
  const updateFunc = (leafNode: ILeafNode): ILeafNode => updateTabNode(leafNode, tabId, update);
  return updateNode(currentNode, path, updateFunc);
};
export const updateTreeAppendTabToEnd = (
  currentNode: IMosaicNode,
  path: string[],
  nodeToAdd: IMosaicTabNode
): IMosaicNode => {
  const updateFunc = (leafNode: ILeafNode): ILeafNode => appendTabToEnd(leafNode, nodeToAdd);
  return updateNode(currentNode, path, updateFunc);
};

export const updateTreeInsertTab = (
  currentNode: IMosaicNode,
  path: string[],
  nodeToAdd: IMosaicTabNode,
  insertDirection: MosaicDirection,
  splitPercentage: number
): IMosaicNode => {
  const updateFunc = (leafNode: ILeafNode): IParentNode =>
    insertTab(leafNode, nodeToAdd, insertDirection, splitPercentage);
  return updateNode(currentNode, path, updateFunc);
};

export const updateTreeAddTab = (
  currentNode: IMosaicNode,
  path: string[],
  nodeToAdd: IMosaicTabNode,
  hoverKey: IMosaicTabNode
): IMosaicNode => {
  const updateFunc = (leafNode: ILeafNode): ILeafNode => addTab(leafNode, nodeToAdd, hoverKey);
  return updateNode(currentNode, path, updateFunc);
};

export const updateTreeSetActiveIndex = (
  currentNode: IMosaicNode,
  path: string[],
  activeIndex: number
): IMosaicNode => {
  const updateFunc = (leafNode: ILeafNode): ILeafNode => setActiveIndex(leafNode, activeIndex);
  return updateNode(currentNode, path, updateFunc);
};

export const updateTreeMoveTabToEnd = (
  currentNode: IMosaicNode,
  path: string[],
  nodeToAdd: IMosaicTabNode
): IMosaicNode => {
  const updateFunc = (leafNode: ILeafNode): ILeafNode => moveTabNodeToEnd(leafNode, nodeToAdd);
  return updateNode(currentNode, path, updateFunc);
};

/* UTILTITY TO FLATTEN MOSAIC TREE */
export const flattenTree = (currentNode: IMosaicNode | null): IMosaicNode | null => {
  if (currentNode) {
    if (isLeafNode(currentNode)) {
      return { ...currentNode };
    }
    const left = flattenTree(currentNode.first);
    const right = flattenTree(currentNode.second);

    if (left && right) {
      return {
        ...currentNode,
        first: left,
        second: right,
      };
    }
    if (!left && !right) {
      return { ...currentNode };
    } else if (!left && right) {
      return { ...right };
    } else if (left && !right) {
      return { ...left };
    }
  }
  return null;
};

export const getFirstTypedNodePath = (
  currentNode: IMosaicNode | null,
  path: string[],
  type: IMosaicTabNode["type"]
): string[] | null => {
  if (!currentNode) {
    return null;
  }

  if (isLeafNode(currentNode)) {
    const isContext = currentNode.tabNodes.some((value: IMosaicTabNode) => value.type === type);
    return isContext ? path : null;
  }
  const left = getFirstTypedNodePath(currentNode.first, [...path, "first"], type);
  const right = getFirstTypedNodePath(currentNode.second, [...path, "second"], type);

  if (!left && !right) {
    return null;
  } else if (left) {
    return left;
  } else {
    return right;
  }
};

/**
 * Find the path to the first leaf whose tabs are *all* of the given types.
 * Used for side-panel grouping: matches a leaf only when it's dedicated to those
 * types, so context tabs don't pollute a mixed/content leaf.
 */
export const getFirstFullyTypedNodePath = (
  currentNode: IMosaicNode | null,
  path: string[],
  types: IMosaicTabNode["type"][]
): string[] | null => {
  if (!currentNode) {
    return null;
  }

  if (isLeafNode(currentNode)) {
    if (currentNode.tabNodes.length === 0) {
      return null;
    }
    return currentNode.tabNodes.every((value: IMosaicTabNode) => types.includes(value.type)) ? path : null;
  }
  const left = getFirstFullyTypedNodePath(currentNode.first, [...path, "first"], types);
  const right = getFirstFullyTypedNodePath(currentNode.second, [...path, "second"], types);
  return left ?? right;
};

export const getFirstValidNodePath = (currentNode: IMosaicNode | null, path: string[]): string[] | null => {
  if (!currentNode) {
    return null;
  }

  if (isLeafNode(currentNode)) {
    return path;
  }
  const left = getFirstValidNodePath(currentNode.first, [...path, "first"]);
  const right = getFirstValidNodePath(currentNode.second, [...path, "second"]);

  if (!left && !right) {
    return null;
  } else if (left) {
    return left;
  } else {
    return right;
  }
};

export interface IFoundMosaicNode {
  node: ILeafNode;
  foundIndex: number;
  path: string[];
}

export const findMosaicNode = (
  currentNode: IMosaicNode | null,
  path: string[],
  viewId: ViewId,
  props: any
): IFoundMosaicNode | null => {
  if (!currentNode) {
    return null;
  }
  if (isLeafNode(currentNode)) {
    const foundIndex = currentNode.tabNodes.findIndex(
      (tabNode: IMosaicTabNode) => tabNode.componentName === viewId && isEqual(tabNode.props, props)
    );
    return foundIndex >= 0 ? { node: currentNode, foundIndex, path } : null;
  }
  const left = findMosaicNode(currentNode.first, [...path, "first"], viewId, props);
  const right = findMosaicNode(currentNode.second, [...path, "second"], viewId, props);

  if (left || right) {
    return left ? left : right;
  }
  return null;
};

/**
 * Find if there are any windows of a certain component name open
 * @param currentNode - the current node tree
 * @param componentName - the name of the component you want to check on
 * @returns
 */
export const findMosaicNodeByComponentName = (
  currentNode: IMosaicNode | null,
  componentName: string,
  componentTitle?: string
): boolean => {
  if (!currentNode) {
    return false;
  }

  if (isLeafNode(currentNode)) {
    const foundIndex = currentNode.tabNodes.findIndex((tabNode: IMosaicTabNode) =>
      componentTitle
        ? tabNode.componentName === componentName && tabNode.title === componentTitle
        : tabNode.componentName === componentName
    );
    return foundIndex >= 0;
  }
  const left = findMosaicNodeByComponentName(currentNode.first, componentName);
  const right = findMosaicNodeByComponentName(currentNode.second, componentName);

  if (left || right) {
    return left ? left : right;
  }
  return false;
};

/**
 * Find the nodes that have the same view id and tab id
 * @param currentNode - the current nodes
 * @param viewId - the name of the window ie HistoryWindow
 * @param tabId - the reandom id OR the id you passed in then opening the window
 * @returns
 */
export const findMosaicNodeByIds = (
  currentNode: IMosaicNode | null,
  viewId: ViewId,
  tabId: string
): IFoundMosaicNode | null => {
  if (!currentNode) {
    return null;
  }
  if (isLeafNode(currentNode)) {
    const foundIndex = currentNode.tabNodes.findIndex(
      (tabNode: IMosaicTabNode) => tabNode.componentName === viewId && tabNode.id === tabId
    );
    return foundIndex >= 0 ? { node: currentNode, foundIndex, path: [] } : null;
  }
  const left = findMosaicNodeByIds(currentNode.first, viewId, tabId);
  const right = findMosaicNodeByIds(currentNode.second, viewId, tabId);

  if (left || right) {
    return left ? left : right;
  }
  return null;
};

/**
 * New Function used to search the mosaic node tree (or part of it) for the existence of one or more nodes.
 * @param currentNode - The part of the nodeTree you want to search.
 * @param path - Path to search in the above node.
 * @param viewId - ViewId of the item you're looking for
 * @param props - Optional partial props to match. Will return results as long as they contain this.
 */
export const findMosaicNodes = (
  currentNode: IMosaicNode | null,
  path: string[],
  viewId: ViewId,
  props?: any
): IFoundMosaicNode[] | null => {
  if (!currentNode) {
    return null;
  }
  // Return all node results from leaf node.
  const matchProps = props ?? {};
  if (isLeafNode(currentNode)) {
    const nodesFound: IFoundMosaicNode[] = currentNode.tabNodes.reduce(
      (results: IFoundMosaicNode[], tabNode: IMosaicTabNode, foundIndex: number) => {
        const match =
          tabNode.componentName === viewId &&
          Object.entries(matchProps).every(([key, value]) => tabNode.props[key] === value);
        if (match) {
          const foundNode: IFoundMosaicNode = { node: currentNode, foundIndex, path };
          results.push(foundNode);
        }
        return results;
      },
      []
    );
    return nodesFound ?? [];
  } else {
    // Return all node results from parent nodes.
    const left = findMosaicNodes(currentNode.first, [...path, "first"], viewId, props);
    const right = findMosaicNodes(currentNode.second, [...path, "second"], viewId, props);
    const nodes = [];
    if (left) {
      nodes.push(...left);
    }
    if (right) {
      nodes.push(...right);
    }
    return nodes;
  }
};

// Get node from a path
const getNode = (currentNode: IMosaicNode | null, path: string[]): IMosaicNode | null => {
  if (!currentNode) {
    return null;
  }

  if (isLeafNode(currentNode) || path.length === 0) {
    return currentNode;
  }
  // If we are here path is not empty so we need to keep going deeper
  const direction = path.shift();
  if (direction === "first") {
    return getNode(currentNode.first, path);
  } else if (direction === "second") {
    return getNode(currentNode.second, path);
  }

  return null;
};

const updateNode = (currentNode: IMosaicNode, path: string[], updateFunc: any): IMosaicNode => {
  if (isLeafNode(currentNode)) {
    return updateFunc(currentNode);
  }

  const leafNodeDir: string = path.pop() ?? "";
  const parentNode = getNode(currentNode, path);

  if (isParentNode(parentNode)) {
    let leafNode;
    if (leafNodeDir === "first") {
      leafNode = { ...parentNode.first };
    } else if (leafNodeDir === "second") {
      leafNode = { ...parentNode.second };
    } else {
      throw new Error("ERROR: Invalid direction, should be 'first' or 'second'.");
    }

    if (isLeafNode(leafNode)) {
      const updatedLeafNode = updateFunc(leafNode);
      if (leafNodeDir === "first") {
        parentNode.first = updatedLeafNode;
      } else if (leafNodeDir === "second") {
        parentNode.second = updatedLeafNode;
      }
    } else {
      throw new Error("ERROR: Child of parent node should be leaf node!");
    }
  }

  return currentNode;
};

// Actual tree updates
const removeTab = (leafNode: ILeafNode, keyToRemove: string): ILeafNode | null => {
  const keyIndex = leafNode.tabNodes.findIndex((input: IMosaicTabNode) => input.id !== keyToRemove);
  const arr = leafNode.tabNodes.filter((input: IMosaicTabNode) => input.id !== keyToRemove);
  const activeIndex = leafNode.activeIndex >= keyIndex ? arr.length - 1 : leafNode.activeIndex;
  // If the removed element was the only one, return null (To be flattened afterwards)
  return arr.length > 0 ? { ...leafNode, activeIndex, tabNodes: arr } : null;
};

const appendTabToEnd = (leafNode: ILeafNode, nodeToAdd: IMosaicTabNode): ILeafNode => {
  return {
    ...leafNode,
    tabNodes: [...leafNode.tabNodes, { ...nodeToAdd }],
    activeIndex: leafNode.tabNodes.length,
  };
};

const updateTabNode = (leafNode: ILeafNode, tabId: string, update: Partial<IMosaicTabNode>): ILeafNode => {
  const index = leafNode.tabNodes.findIndex((node: IMosaicTabNode) => node.id === tabId);
  if (index === -1) return leafNode;
  // Replace the node entirely with the update
  leafNode.tabNodes.splice(index, 1, {
    ...leafNode.tabNodes[index],
    ...update,
  });
  return leafNode;
};

const insertTab = (
  leafNode: ILeafNode,
  nodeToAdd: IMosaicTabNode,
  insertDirection: MosaicDirection,
  splitPercentage = 50
): IParentNode => {
  return getLeafNodeFromDirection(leafNode, nodeToAdd, insertDirection, splitPercentage);
};

const addTab = (leafNode: ILeafNode, nodeToAdd: IMosaicTabNode, hoverKey: IMosaicTabNode | null): ILeafNode => {
  const index = hoverKey
    ? leafNode.tabNodes.findIndex((currentNode: IMosaicTabNode) => {
        return currentNode.id === hoverKey.id;
      })
    : leafNode.tabNodes.length;

  if (index < 0) {
    leafNode.activeIndex = 0;
    leafNode.tabNodes.push(nodeToAdd);
  } else {
    leafNode.activeIndex = index;
    leafNode.tabNodes.splice(index, 0, nodeToAdd);
  }

  return leafNode;
};

const moveTabNodeToEnd = (leafNode: ILeafNode, nodeToAdd: IMosaicTabNode): ILeafNode => {
  const index = leafNode.tabNodes.findIndex((tabNode: IMosaicTabNode) => tabNode.id === nodeToAdd.id);
  leafNode.tabNodes.splice(index, 1);
  leafNode.tabNodes.push(nodeToAdd);
  return leafNode;
};

const getLeafNodeFromDirection = (
  leafNode: ILeafNode,
  tabNodeToInsert: IMosaicTabNode,
  insertDirection: MosaicDirection,
  splitPercentage = 50
): IParentNode => {
  if (insertDirection === MosaicDirection.Center) {
    return {
      first: {
        ...leafNode,
        activeIndex: leafNode.tabNodes.length,
        tabNodes: [...leafNode.tabNodes, { ...tabNodeToInsert }],
      },
      second: null,
      direction: SplitDirection.Horizontal,
      splitPercentage,
      id: v1(),
    };
  } else if (insertDirection === MosaicDirection.Left || insertDirection === MosaicDirection.Top) {
    return {
      first: {
        id: v1(),
        activeIndex: 0,
        tabNodes: [{ ...tabNodeToInsert }],
      },
      second: leafNode,
      direction: insertDirection === MosaicDirection.Left ? SplitDirection.Horizontal : SplitDirection.Vertical,
      splitPercentage: 100 - splitPercentage,
      id: v1(),
    };
  } else {
    return {
      first: leafNode,
      second: {
        id: v1(),
        activeIndex: 0,
        tabNodes: [{ ...tabNodeToInsert }],
      },
      direction: insertDirection === MosaicDirection.Right ? SplitDirection.Horizontal : SplitDirection.Vertical,
      splitPercentage,
      id: v1(),
    };
  }
};

const setActiveIndex = (leafNode: ILeafNode, activeIndex: number): ILeafNode => {
  leafNode.activeIndex = activeIndex;
  return leafNode;
};
